import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Input,
  List,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileProtectOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  TeamOutlined,
  WarningOutlined
} from '@ant-design/icons';
import Layout from '../../layout/Layout';
import { fetchComboData } from '../../services/api';
import {
  checkPayrollApiConnection,
  getAttendanceReport,
  getDeductionsAnalysis,
  getEmployeesWithDetails,
  getMonthDateRange,
  getMonthlyPayrollReport,
  getPayrollTrends,
  getSummaryReport,
  getYearlyPayrollSummary
} from '../../services/payrollReportsApi';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const toNumber = (value) => Number.parseFloat(value) || 0;

const years = Array.from({ length: 7 }, (_, index) => new Date().getFullYear() - 3 + index);

const normalizeDepartments = (value) => {
  return (Array.isArray(value) ? value : [])
    .map((department, index) => ({
      id: department?.id ?? department?.department_id ?? `department-${index}`,
      department_name: department?.department_name ?? department?.name ?? String(department?.id ?? '')
    }))
    .filter((department) => department.department_name);
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(toNumber(value));
};

const formatMonthValue = (value) => {
  if (!value) {
    return '-';
  }

  const [year, month] = String(value).split('-');
  const monthLabel = months.find((entry) => entry.value === Number(month))?.label;
  return monthLabel ? `${monthLabel} ${year}` : String(value);
};

const formatScopedPayrollMonth = (record, fallbackYear) => {
  const monthValue = record?.payroll_month;

  if (monthValue === null || monthValue === undefined || monthValue === '') {
    return '-';
  }

  if (String(monthValue).includes('-')) {
    return formatMonthValue(monthValue);
  }

  return formatMonthValue(`${record?.payroll_year || fallbackYear}-${String(monthValue).padStart(2, '0')}`);
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Healthy':
    case 'Covered':
    case 'Ready':
    case 'On Track':
      return 'success';
    case 'Monitor':
    case 'Review':
      return 'warning';
    case 'Risk':
    case 'Missing':
      return 'error';
    default:
      return 'default';
  }
};

const prettifyKey = (value) => {
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const stringifyRecord = (record) => {
  return Object.values(record || {})
    .filter((value) => value !== null && value !== undefined)
    .join(' ')
    .toLowerCase();
};

const downloadCsv = (rows, filename) => {
  if (!rows.length) {
    message.warning('No rows available to download.');
    return;
  }

  const headers = Array.from(
    rows.reduce((keys, row) => {
      Object.keys(row).forEach((key) => {
        if (!['key', 'actions'].includes(key)) {
          keys.add(key);
        }
      });
      return keys;
    }, new Set())
  );

  const csv = [
    headers.map((header) => `"${prettifyKey(header)}"`).join(','),
    ...rows.map((row) => {
      return headers
        .map((header) => {
          const rawValue = row[header];
          const value = rawValue === null || rawValue === undefined ? '' : String(rawValue);
          return `"${value.replace(/"/g, '""')}"`;
        })
        .join(',');
    })
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const resolveDepartmentName = (row, departments) => {
  if (row.department_name) {
    return row.department_name;
  }

  const matchedDepartment = departments.find((department) => String(department.id) === String(row.department_id));
  return matchedDepartment?.department_name || `Department ${row.department_id || '-'}`;
};

const TaxCompliances = () => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [reportTab, setReportTab] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [apiError, setApiError] = useState('');
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [deductionRows, setDeductionRows] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [yearlyRows, setYearlyRows] = useState([]);
  const [trendRows, setTrendRows] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [employeeRows, setEmployeeRows] = useState([]);
  const [detailRecord, setDetailRecord] = useState(null);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const departmentData = await fetchComboData('employee_departments', 'department_name');
        setDepartments(normalizeDepartments(departmentData));
      } catch (error) {
        console.error('Failed to load departments:', error);
        setDepartments([]);
      }
    };

    loadDepartments();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadDashboardData = async () => {
      setLoading(true);
      setApiError('');

      const isApiReachable = await checkPayrollApiConnection();

      if (!isMounted) {
        return;
      }

      if (!isApiReachable) {
        setMonthlyRows([]);
        setDeductionRows([]);
        setAttendanceRows([]);
        setYearlyRows([]);
        setTrendRows([]);
        setSummaryRows([]);
        setEmployeeRows([]);
        setApiError('Payroll API is offline. Start the backend server on port 4402 and refresh this page.');
        setLoading(false);
        return;
      }

      const { startDate, endDate } = getMonthDateRange(selectedYear, selectedMonth);
      const requests = [
        { key: 'monthly', label: 'monthly payroll', request: getMonthlyPayrollReport(selectedYear, selectedMonth, selectedDepartment) },
        { key: 'deductions', label: 'deductions', request: getDeductionsAnalysis(selectedDepartment, startDate, endDate) },
        { key: 'attendance', label: 'attendance', request: getAttendanceReport(selectedDepartment, startDate, endDate) },
        { key: 'yearly', label: 'yearly summary', request: getYearlyPayrollSummary(selectedYear, selectedDepartment) },
        { key: 'trends', label: 'trends', request: getPayrollTrends(selectedDepartment) },
        { key: 'summary', label: 'dashboard summary', request: getSummaryReport() },
        { key: 'employees', label: 'employees', request: getEmployeesWithDetails('Active', selectedDepartment) }
      ];

      const results = await Promise.allSettled(requests.map((entry) => entry.request));

      if (!isMounted) {
        return;
      }

      const failures = [];
      const nextData = {
        monthly: [],
        deductions: [],
        attendance: [],
        yearly: [],
        trends: [],
        summary: [],
        employees: []
      };

      results.forEach((result, index) => {
        const requestMeta = requests[index];

        if (result.status === 'rejected') {
          failures.push(requestMeta.label);
          console.error(`Failed to load ${requestMeta.label}:`, result.reason);
          return;
        }

        const payload = result.value;
        if (payload?.success === false) {
          failures.push(requestMeta.label);
          return;
        }

        if (requestMeta.key === 'monthly') {
          nextData.monthly = payload?.data?.records || [];
        }

        if (requestMeta.key === 'deductions') {
          nextData.deductions = payload?.data?.records || [];
        }

        if (requestMeta.key === 'attendance') {
          nextData.attendance = payload?.data?.records || [];
        }

        if (requestMeta.key === 'yearly') {
          nextData.yearly = payload?.data?.departments || [];
        }

        if (requestMeta.key === 'trends') {
          nextData.trends = payload?.data?.trends || [];
        }

        if (requestMeta.key === 'summary') {
          nextData.summary = payload?.data?.comparison || [];
        }

        if (requestMeta.key === 'employees') {
          nextData.employees = Array.isArray(payload?.data) ? payload.data : [];
        }
      });

      setMonthlyRows(nextData.monthly);
      setDeductionRows(nextData.deductions);
      setAttendanceRows(nextData.attendance);
      setYearlyRows(nextData.yearly);
      setTrendRows(nextData.trends);
      setSummaryRows(nextData.summary);
      setEmployeeRows(nextData.employees);

      if (failures.length > 0) {
        setApiError(`Some datasets could not be loaded: ${failures.join(', ')}.`);
      }

      setLoading(false);
    };

    loadDashboardData();

    return () => {
      isMounted = false;
    };
  }, [selectedDepartment, selectedMonth, selectedYear]);

  const scopedMonthlyRows = useMemo(() => {
    return monthlyRows.map((row, index) => ({
      key: `monthly-${row.id || index}`,
      employee_name: row.employee_name,
      employee_number: row.employee_number,
      department_name: resolveDepartmentName(row, departments),
      position: row.position,
      payroll_month: row.payroll_month,
      payroll_year: row.payroll_year,
      payroll_period: row.payroll_period,
      batch_number: row.batch_number,
      monthly_salary: toNumber(row.monthly_salary),
      gross_salary: toNumber(row.gross_salary),
      sso_deduction: toNumber(row.sso_deduction),
      total_deductions: toNumber(row.total_deductions),
      net_salary: toNumber(row.net_salary),
      source_type: 'monthly'
    }));
  }, [departments, monthlyRows]);

  const scopedSsoRows = useMemo(() => {
    return monthlyRows
      .filter((row) => toNumber(row.sso_deduction) > 0)
      .map((row, index) => ({
        key: `sso-${row.id || index}`,
        employee_name: row.employee_name,
        employee_number: row.employee_number,
        department_name: resolveDepartmentName(row, departments),
        payroll_month: row.payroll_month,
        batch_number: row.batch_number,
        sso_deduction: toNumber(row.sso_deduction),
        total_deductions: toNumber(row.total_deductions),
        net_salary: toNumber(row.net_salary),
        source_type: 'sso'
      }));
  }, [departments, monthlyRows]);

  const scopedDeductionRows = useMemo(() => {
    return deductionRows.map((row, index) => ({
      key: `deduction-${index}`,
      employee_name: row.employee_name,
      employee_number: row.employee_number,
      department_name: row.department_name,
      position: row.position,
      payroll_month: row.payroll_month,
      sso_deduction: toNumber(row.sso_deduction),
      late_penalty: toNumber(row.late_penalty),
      advance_deduction: toNumber(row.advance_deduction),
      pending_deduction: toNumber(row.pending_deduction),
      cut_deduction: toNumber(row.cut_deduction),
      total_deductions: toNumber(row.total_deductions),
      source_type: 'deductions'
    }));
  }, [deductionRows]);

  const scopedAttendanceRows = useMemo(() => {
    return attendanceRows.map((row, index) => ({
      key: `attendance-${index}`,
      employee_name: row.employee_name,
      employee_number: row.employee_number,
      department_name: row.department_name,
      position: row.position,
      payroll_month: row.payroll_month,
      work_days: toNumber(row.work_days),
      absent_days: toNumber(row.absent_days),
      attendance_percentage: toNumber(row.attendance_percentage),
      late_penalty: toNumber(row.late_penalty),
      source_type: 'attendance'
    }));
  }, [attendanceRows]);

  const scopedYearlyRows = useMemo(() => {
    return yearlyRows.map((row, index) => ({
      key: `yearly-${index}`,
      department_name: row.department_name,
      total_employees: toNumber(row.total_employees),
      total_salary_budget: toNumber(row.total_salary_budget),
      total_deductions: toNumber(row.total_deductions),
      total_net_salary: toNumber(row.total_net_salary),
      average_salary: toNumber(row.average_salary),
      total_absent_days: toNumber(row.total_absent_days),
      year: row.year || selectedYear,
      source_type: 'yearly'
    }));
  }, [selectedYear, yearlyRows]);

  const filteredSections = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filterRows = (rows) => {
      if (!normalizedSearch) {
        return rows;
      }

      return rows.filter((row) => stringifyRecord(row).includes(normalizedSearch));
    };

    return [
      {
        key: 'monthly',
        label: 'Monthly Payroll',
        description: 'Live payroll records for the selected month and department scope, sorted by monthly salary.',
        rows: filterRows(scopedMonthlyRows).slice().sort((left, right) => right.monthly_salary - left.monthly_salary)
      },
      {
        key: 'sso',
        label: 'SSO Coverage',
        description: 'Employees with real SSO deductions captured in payroll records.',
        rows: filterRows(scopedSsoRows)
      },
      {
        key: 'deductions',
        label: 'Deductions',
        description: 'Detailed live deduction rows from payroll records.',
        rows: filterRows(scopedDeductionRows)
      },
      {
        key: 'attendance',
        label: 'Attendance Risk',
        description: 'Attendance and penalty data for compliance follow-up.',
        rows: filterRows(scopedAttendanceRows)
      },
      {
        key: 'yearly',
        label: 'Yearly Summary',
        description: 'Department-wise yearly payroll totals from the database.',
        rows: filterRows(scopedYearlyRows)
      }
    ];
  }, [scopedAttendanceRows, scopedDeductionRows, scopedMonthlyRows, scopedSsoRows, scopedYearlyRows, searchTerm]);

  const activeSection = filteredSections.find((section) => section.key === reportTab) || filteredSections[0];

  const activeEmployees = employeeRows.length;
  const payrollEmployeeCount = new Set(monthlyRows.map((row) => row.employee_id || row.employee_number).filter(Boolean)).size;
  const ssoCoveredCount = new Set(monthlyRows.filter((row) => toNumber(row.sso_deduction) > 0).map((row) => row.employee_id || row.employee_number).filter(Boolean)).size;
  const attendanceIssueCount = attendanceRows.filter((row) => toNumber(row.absent_days) > 0 || toNumber(row.late_penalty) > 0).length;
  const departmentCount = new Set((monthlyRows.length ? monthlyRows : yearlyRows).map((row) => row.department_name || row.department_id).filter(Boolean)).size;
  const totalDeductions = deductionRows.reduce((total, row) => total + toNumber(row.total_deductions), 0);
  const totalNetSalary = monthlyRows.reduce((total, row) => total + toNumber(row.net_salary), 0);
  const ssoCoveragePercent = activeEmployees > 0 ? Math.round((ssoCoveredCount / activeEmployees) * 100) : 0;
  const latestTrend = trendRows[0] || null;
  const currentSummary = summaryRows.find((row) => row.period === 'Current Month') || summaryRows[0] || null;
  const selectedMonthLabel = months.find((entry) => entry.value === selectedMonth)?.label;
  const nextMonthDueDate = new Date(selectedYear, selectedMonth, 7).toISOString().slice(0, 10);
  const ssoDueDate = new Date(selectedYear, selectedMonth, 15).toISOString().slice(0, 10);

  const openDetailModal = (record) => {
    setDetailRecord(record);
  };

  const downloadRecord = (record) => {
    const reportName = record.source_type || 'report';
    const identifier = record.employee_number || record.department_name || 'record';
    downloadCsv([record], `${reportName}-${identifier}.csv`);
  };

  const withActions = (columns) => {
    return [
      ...columns,
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        fixed: 'right',
        render: (_, record) => (
          <Space>
            <Button type="text" icon={<EyeOutlined />} onClick={() => openDetailModal(record)} />
            <Button type="text" icon={<DownloadOutlined />} onClick={() => downloadRecord(record)} />
          </Space>
        )
      }
    ];
  };

  const monthlyColumns = withActions([
    { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name', render: (value, record) => <Space direction="vertical" size={0}><Text strong>{value}</Text><Text type="secondary">{record.employee_number}</Text></Space> },
    { title: 'Department', dataIndex: 'department_name', key: 'department_name' },
    { title: 'Position', dataIndex: 'position', key: 'position' },
    { title: 'Monthly Salary', dataIndex: 'monthly_salary', key: 'monthly_salary', defaultSortOrder: 'descend', sorter: (left, right) => left.monthly_salary - right.monthly_salary, render: (value) => formatCurrency(value) },
    { title: 'Gross Salary', dataIndex: 'gross_salary', key: 'gross_salary', render: (value) => formatCurrency(value) },
    { title: 'Payroll Month', dataIndex: 'payroll_month', key: 'payroll_month', render: (_, record) => formatScopedPayrollMonth(record, selectedYear) },
    { title: 'Period', dataIndex: 'payroll_period', key: 'payroll_period' },
    { title: 'Batch', dataIndex: 'batch_number', key: 'batch_number' },
    { title: 'SSO', dataIndex: 'sso_deduction', key: 'sso_deduction', render: (value) => formatCurrency(value) },
    { title: 'Deductions', dataIndex: 'total_deductions', key: 'total_deductions', render: (value) => formatCurrency(value) },
    { title: 'Net Salary', dataIndex: 'net_salary', key: 'net_salary', render: (value) => formatCurrency(value) }
  ]);

  const ssoColumns = withActions([
    { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name', render: (value, record) => <Space direction="vertical" size={0}><Text strong>{value}</Text><Text type="secondary">{record.employee_number}</Text></Space> },
    { title: 'Department', dataIndex: 'department_name', key: 'department_name' },
    { title: 'Payroll Month', dataIndex: 'payroll_month', key: 'payroll_month', render: (_, record) => formatScopedPayrollMonth(record, selectedYear) },
    { title: 'Batch', dataIndex: 'batch_number', key: 'batch_number' },
    { title: 'SSO Deduction', dataIndex: 'sso_deduction', key: 'sso_deduction', render: (value) => formatCurrency(value) },
    { title: 'Net Salary', dataIndex: 'net_salary', key: 'net_salary', render: (value) => formatCurrency(value) }
  ]);

  const deductionColumns = withActions([
    { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name', render: (value, record) => <Space direction="vertical" size={0}><Text strong>{value}</Text><Text type="secondary">{record.employee_number}</Text></Space> },
    { title: 'Department', dataIndex: 'department_name', key: 'department_name' },
    { title: 'Position', dataIndex: 'position', key: 'position' },
    { title: 'Payroll Month', dataIndex: 'payroll_month', key: 'payroll_month', render: (_, record) => formatScopedPayrollMonth(record, selectedYear) },
    { title: 'SSO', dataIndex: 'sso_deduction', key: 'sso_deduction', render: (value) => formatCurrency(value) },
    { title: 'Advance', dataIndex: 'advance_deduction', key: 'advance_deduction', render: (value) => formatCurrency(value) },
    { title: 'Pending', dataIndex: 'pending_deduction', key: 'pending_deduction', render: (value) => formatCurrency(value) },
    { title: 'Late Penalty', dataIndex: 'late_penalty', key: 'late_penalty', render: (value) => formatCurrency(value) },
    { title: 'Total', dataIndex: 'total_deductions', key: 'total_deductions', render: (value) => formatCurrency(value) }
  ]);

  const attendanceColumns = withActions([
    { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name', render: (value, record) => <Space direction="vertical" size={0}><Text strong>{value}</Text><Text type="secondary">{record.employee_number}</Text></Space> },
    { title: 'Department', dataIndex: 'department_name', key: 'department_name' },
    { title: 'Position', dataIndex: 'position', key: 'position' },
    { title: 'Payroll Month', dataIndex: 'payroll_month', key: 'payroll_month', render: (_, record) => formatScopedPayrollMonth(record, selectedYear) },
    { title: 'Work Days', dataIndex: 'work_days', key: 'work_days' },
    { title: 'Absent Days', dataIndex: 'absent_days', key: 'absent_days' },
    { title: 'Attendance %', dataIndex: 'attendance_percentage', key: 'attendance_percentage', render: (value) => `${toNumber(value).toFixed(2)}%` },
    { title: 'Late Penalty', dataIndex: 'late_penalty', key: 'late_penalty', render: (value) => formatCurrency(value) }
  ]);

  const yearlyColumns = withActions([
    { title: 'Department', dataIndex: 'department_name', key: 'department_name' },
    { title: 'Employees', dataIndex: 'total_employees', key: 'total_employees' },
    { title: 'Salary Budget', dataIndex: 'total_salary_budget', key: 'total_salary_budget', render: (value) => formatCurrency(value) },
    { title: 'Deductions', dataIndex: 'total_deductions', key: 'total_deductions', render: (value) => formatCurrency(value) },
    { title: 'Net Salary', dataIndex: 'total_net_salary', key: 'total_net_salary', render: (value) => formatCurrency(value) },
    { title: 'Average Salary', dataIndex: 'average_salary', key: 'average_salary', render: (value) => formatCurrency(value) },
    { title: 'Absent Days', dataIndex: 'total_absent_days', key: 'total_absent_days' },
    { title: 'Year', dataIndex: 'year', key: 'year' }
  ]);

  const sectionColumns = {
    monthly: monthlyColumns,
    sso: ssoColumns,
    deductions: deductionColumns,
    attendance: attendanceColumns,
    yearly: yearlyColumns
  };

  const detailItems = Object.entries(detailRecord || {}).filter(([key]) => key !== 'key' && key !== 'source_type');

  const reportTabItems = filteredSections.map((section) => ({
    key: section.key,
    label: `${section.label} (${section.rows.length})`,
    children: (
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Alert type="info" showIcon message={section.label} description={section.description} />
        <Table
          rowKey="key"
          columns={sectionColumns[section.key]}
          dataSource={section.rows}
          size="small"
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 8, hideOnSinglePage: section.rows.length <= 8 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No realtime rows found for the selected filters."
              />
            )
          }}
        />
      </Space>
    )
  }));

  const complianceItems = [
    {
      label: 'Active employees in selected scope',
      value: activeEmployees,
      status: activeEmployees > 0 ? 'Healthy' : 'Review'
    },
    {
      label: 'Employees covered by payroll rows',
      value: payrollEmployeeCount,
      status: payrollEmployeeCount === activeEmployees && activeEmployees > 0 ? 'Covered' : 'Review'
    },
    {
      label: 'Employees with SSO deduction',
      value: `${ssoCoveredCount} (${ssoCoveragePercent}%)`,
      status: ssoCoveragePercent >= 90 ? 'Covered' : ssoCoveragePercent >= 70 ? 'Monitor' : 'Risk'
    },
    {
      label: 'Attendance issues in selected month',
      value: attendanceIssueCount,
      status: attendanceIssueCount === 0 ? 'Healthy' : attendanceIssueCount <= 5 ? 'Monitor' : 'Risk'
    },
    {
      label: 'Departments processed',
      value: departmentCount,
      status: departmentCount > 0 ? 'On Track' : 'Review'
    }
  ];

  return (
    <Layout>
      <div className="container-fluid p-0">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card bordered={false}>
              <Space direction="vertical" size={20} style={{ width: '100%' }}>
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                  <Col xs={24} lg={15}>
                    <Space direction="vertical" size={4}>
                      <Title level={3} style={{ margin: 0 }}>Tax and Compliance Center</Title>
                      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        Live payroll, SSO, deduction, attendance, and yearly compliance reporting from the database.
                      </Paragraph>
                    </Space>
                  </Col>
                  <Col xs={24} lg={9}>
                    <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
                      <Button
                        icon={<FileSearchOutlined />}
                        onClick={() => {
                          if (!activeSection?.rows?.length) {
                            message.warning('No report rows available to review.');
                            return;
                          }
                          openDetailModal(activeSection.rows[0]);
                        }}
                      >
                        View Report
                      </Button>
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => downloadCsv(activeSection?.rows || [], `${activeSection?.key || 'report'}-${selectedYear}-${selectedMonth}.csv`)}
                      >
                        Download
                      </Button>
                    </Space>
                  </Col>
                </Row>

                {apiError ? <Alert type="warning" showIcon message={apiError} /> : null}

                <Card size="small" bordered={false}>
                  <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={6}>
                      <Select
                        value={selectedMonth}
                        onChange={setSelectedMonth}
                        style={{ width: '100%' }}
                        options={months.map((month) => ({ value: month.value, label: month.label }))}
                      />
                    </Col>
                    <Col xs={24} md={4}>
                      <Select
                        value={selectedYear}
                        onChange={setSelectedYear}
                        style={{ width: '100%' }}
                        options={years.map((year) => ({ value: year, label: String(year) }))}
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <Select
                        allowClear
                        placeholder="All departments"
                        value={selectedDepartment}
                        onChange={setSelectedDepartment}
                        style={{ width: '100%' }}
                        options={departments.map((department) => ({
                          value: department.id,
                          label: department.department_name
                        }))}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Search
                        allowClear
                        placeholder="Search live report rows"
                        prefix={<SearchOutlined />}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                      />
                    </Col>
                  </Row>
                </Card>

                <Spin spinning={loading}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} xl={6}>
                      <Card bordered={false} size="small">
                        <Statistic title="Payroll Employees" value={payrollEmployeeCount} prefix={<TeamOutlined />} />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} xl={6}>
                      <Card bordered={false} size="small">
                        <Statistic title="SSO Covered" value={ssoCoveredCount} prefix={<SafetyCertificateOutlined />} />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} xl={6}>
                      <Card bordered={false} size="small">
                        <Statistic title="Total Deductions" value={totalDeductions} formatter={(value) => formatCurrency(value)} prefix={<FileProtectOutlined />} />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} xl={6}>
                      <Card bordered={false} size="small">
                        <Statistic title="Net Salary" value={totalNetSalary} formatter={(value) => formatCurrency(value)} prefix={<FileTextOutlined />} />
                      </Card>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={16}>
                      <Tabs
                        items={[
                          {
                            key: 'reports',
                            label: 'Reports',
                            children: <Tabs activeKey={reportTab} onChange={setReportTab} items={reportTabItems} type="card" />
                          },
                          {
                            key: 'compliance',
                            label: 'Compliance',
                            children: (
                              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                <Alert
                                  type="success"
                                  showIcon
                                  message="Realtime compliance snapshot"
                                  description="Statuses below are computed from live payroll, employee, deduction, and attendance datasets for the selected filters."
                                />
                                <Row gutter={[16, 16]}>
                                  <Col xs={24} lg={10}>
                                    <Card title="SSO coverage" bordered={false}>
                                      <Progress percent={ssoCoveragePercent} status={ssoCoveragePercent >= 90 ? 'success' : 'active'} />
                                      <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                                        {ssoCoveredCount} of {activeEmployees || 0} active employees have SSO deductions in {selectedMonthLabel} {selectedYear}.
                                      </Paragraph>
                                    </Card>
                                  </Col>
                                  <Col xs={24} lg={14}>
                                    <Card title="Control checks" bordered={false}>
                                      <List
                                        dataSource={complianceItems}
                                        renderItem={(item) => (
                                          <List.Item>
                                            <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                                              <Space>
                                                {item.status === 'Healthy' || item.status === 'Covered' || item.status === 'On Track' ? (
                                                  <CheckCircleOutlined style={{ color: '#13a35f' }} />
                                                ) : (
                                                  <WarningOutlined style={{ color: '#fa8c16' }} />
                                                )}
                                                <Text>{item.label}</Text>
                                              </Space>
                                              <Space>
                                                <Text strong>{item.value}</Text>
                                                <Tag color={getStatusColor(item.status)}>{item.status}</Tag>
                                              </Space>
                                            </Space>
                                          </List.Item>
                                        )}
                                      />
                                    </Card>
                                  </Col>
                                </Row>
                              </Space>
                            )
                          },
                          {
                            key: 'deadlines',
                            label: 'Deadlines',
                            children: (
                              <Row gutter={[16, 16]}>
                                <Col xs={24} lg={10}>
                                  <Card title="Filing timeline" bordered={false}>
                                    <List
                                      dataSource={[
                                        {
                                          label: 'Payroll close and review',
                                          date: `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-05`,
                                          status: 'Ready'
                                        },
                                        {
                                          label: 'PND / withholding filing checkpoint',
                                          date: nextMonthDueDate,
                                          status: totalDeductions > 0 ? 'Review' : 'Monitor'
                                        },
                                        {
                                          label: 'SSO filing checkpoint',
                                          date: ssoDueDate,
                                          status: ssoCoveredCount > 0 ? 'On Track' : 'Risk'
                                        }
                                      ]}
                                      renderItem={(item) => (
                                        <List.Item>
                                          <Space align="start">
                                            <CalendarOutlined style={{ color: '#1677ff', marginTop: 4 }} />
                                            <Space direction="vertical" size={0}>
                                              <Text strong>{item.label}</Text>
                                              <Text type="secondary">{item.date}</Text>
                                              <Tag color={getStatusColor(item.status)} style={{ width: 'fit-content' }}>{item.status}</Tag>
                                            </Space>
                                          </Space>
                                        </List.Item>
                                      )}
                                    />
                                  </Card>
                                </Col>
                                <Col xs={24} lg={14}>
                                  <Card title="Realtime insight" bordered={false}>
                                    <List
                                      dataSource={[
                                        currentSummary
                                          ? `Current month paid amount: ${formatCurrency(currentSummary.total_paid)} across ${currentSummary.total_employees || 0} employees.`
                                          : 'No current month summary returned by the backend.',
                                        latestTrend
                                          ? `Latest trend month ${formatMonthValue(latestTrend.month_year)} shows ${formatCurrency(latestTrend.total_deductions)} deductions and ${latestTrend.total_absent_days || 0} absent days.`
                                          : 'No trend data returned by the backend.',
                                        yearlyRows.length > 0
                                          ? `${yearlyRows.length} departments are included in the yearly summary for ${selectedYear}.`
                                          : `No yearly summary rows were returned for ${selectedYear}.`
                                      ]}
                                      renderItem={(item) => (
                                        <List.Item>
                                          <Space align="start">
                                            <ClockCircleOutlined style={{ color: '#722ed1', marginTop: 4 }} />
                                            <Text>{item}</Text>
                                          </Space>
                                        </List.Item>
                                      )}
                                    />
                                  </Card>
                                </Col>
                              </Row>
                            )
                          }
                        ]}
                      />
                    </Col>
                    <Col xs={24} lg={8}>
                      <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <Card title="Current scope" bordered={false}>
                          <Descriptions size="small" column={1}>
                            <Descriptions.Item label="Month">{selectedMonthLabel}</Descriptions.Item>
                            <Descriptions.Item label="Year">{selectedYear}</Descriptions.Item>
                            <Descriptions.Item label="Department">
                              {departments.find((department) => String(department.id) === String(selectedDepartment))?.department_name || 'All departments'}
                            </Descriptions.Item>
                          </Descriptions>
                        </Card>
                        <Card title="Attention needed" bordered={false}>
                          <List
                            dataSource={[
                              ...(attendanceRows
                                .filter((row) => toNumber(row.absent_days) > 0 || toNumber(row.late_penalty) > 0)
                                .slice(0, 4)
                                .map((row) => `${row.employee_name} has ${row.absent_days || 0} absent days and ${formatCurrency(row.late_penalty)} late penalty.`)),
                              ...(deductionRows
                                .filter((row) => toNumber(row.total_deductions) > 0)
                                .slice(0, 2)
                                .map((row) => `${row.employee_name} deductions total ${formatCurrency(row.total_deductions)} in ${formatMonthValue(row.payroll_month)}.`))
                            ]}
                            locale={{ emptyText: 'No flagged realtime items for the selected scope.' }}
                            renderItem={(item) => (
                              <List.Item>
                                <Space align="start">
                                  <WarningOutlined style={{ color: '#fa8c16', marginTop: 4 }} />
                                  <Text>{item}</Text>
                                </Space>
                              </List.Item>
                            )}
                          />
                        </Card>
                      </Space>
                    </Col>
                  </Row>
                </Spin>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal
        open={Boolean(detailRecord)}
        title="Report Detail"
        onCancel={() => setDetailRecord(null)}
        footer={[
          <Button key="download" icon={<DownloadOutlined />} onClick={() => detailRecord && downloadRecord(detailRecord)}>
            Download
          </Button>,
          <Button key="close" type="primary" onClick={() => setDetailRecord(null)}>
            Close
          </Button>
        ]}
        width={760}
      >
        <Descriptions bordered size="small" column={1}>
          {detailItems.map(([key, value]) => (
            <Descriptions.Item key={key} label={prettifyKey(key)}>
              {typeof value === 'number' ? value : String(value || '-')}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Modal>
    </Layout>
  );
};

export default TaxCompliances;